import React, { useEffect, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Box } from "theme-ui";

import { deleteServiceArea, getServiceAreas } from "api/ServiceAreas";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import { QuestionMarkIcon, TrashIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import moment from "moment-timezone";
import CreateModal from "./CreateModal";
import EditModal from "./EditModal";
import ServiceAreaMap, { colorForIndex, ServiceAreaMapItem } from "components/ServiceAreaMap";


const columnHelper = createColumnHelper();

const createColumn = (id: string, title: string, accessor: (row: any) => any, cell?: (info: any) => any, options = {}) =>  
  columnHelper.accessor(accessor, { id, cell: cell || ((info) => info.getValue()), header: () => <span>{title}</span>, ...options });

export default function ServiceArea () {  

  const [refreshData, setRefreshData] = useState<boolean>(false);  
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [editServiceArea,setEditServiceArea] = useState<any>(null);

  // Session 30 — map view state. Collapsed by default so the page stays
  // fast and geometry isn't fetched until the user asks for it.
  const [showMap, setShowMap] = useState<boolean>(false);
  const [mapData, setMapData] = useState<ServiceAreaMapItem[] | null>(null);
  const [mapLoading, setMapLoading] = useState<boolean>(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Session 30 follow-up: stable palette assignment keyed by record id.
  // Fetched once on mount from a name-sorted list so every surface —
  // list dot, map polygon, edit modal swatch — shows the SAME color for
  // a given area, regardless of what sort the user has chosen on the
  // table. Without this, the list dot would use the table's current
  // sort index while the map and modal use name-sort, causing a visible
  // color mismatch for areas that have no explicit color saved.
  const [paletteById, setPaletteById] = useState<Record<string, string>>({});

  const toast = useToast();

  const loadPalette = async () => {
    try {
      const res: any = await getServiceAreas({
        query: { recordsPer: "500", sortBy: "name", sortDir: "asc" },
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      const map: Record<string, string> = {};
      items.forEach((it: any, idx: number) => {
        map[it.id] = it.color || colorForIndex(idx);
      });
      setPaletteById(map);
    } catch (e) {
      // Silent fail — falls back to per-row palette derivation.
    }
  };

  const fetchMapData = async () => {
    setMapLoading(true);
    setMapError(null);
    try {
      const res: any = await getServiceAreas({
        query: { includeGeometry: "true", recordsPer: "500", sortBy: "name", sortDir: "asc" },
      });
      const items: ServiceAreaMapItem[] = Array.isArray(res?.items) ? res.items : [];
      setMapData(items);
    } catch (e) {
      setMapError("Could not load map data.");
    } finally {
      setMapLoading(false);
    }
  };

  const toggleMap = async () => {
    const next = !showMap;
    setShowMap(next);
    if (next && !mapData && !mapLoading) {
      await fetchMapData();
    }
  };
              

  const confirmDelete = async (del:any) => { 
    setConfirmSubmit(true);   
    const res = await deleteServiceArea({ data: del });
    if(res?.success){ 
      toast.add("Service area deactivated.","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
      // Re-derive the stable palette map now that the roster changed.
      loadPalette();
      // If the map is currently visible, refetch geometry immediately so
      // the deactivated area disappears without a browser refresh; if
      // it's collapsed, just invalidate the cache for next open.
      if (showMap) {
        fetchMapData();
      } else {
        setMapData(null);
      }
    }else{
      toast.add(res?.message ? res.message : "Error deleting service area.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  

  const deleteModal = async (del?:any) => {
    setConfirmModal(<BasicModal
      title="Deactivate service area?"    
      cancel_click={() => setConfirmModal(false)}
      cancel_text="No, cancel"   
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={<>
        <BasicButton
          type="button"    
          scheme="danger"      
          onClick={() => confirmDelete(del)} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
        >
          {!confirmSubmit && <TrashIcon fill={"#fff"}/>}        
          {"Deactivate"}
        </BasicButton>
      </>}    
    >
      <P>Are you sure you want to deactivate <strong style={{color:"var(--theme-ui-colors-black)"}}>{del?.name}</strong>?</P>
    </BasicModal>);
  }

  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Service Areas"        
    loadPalette();
  }, []); 

  return (
    <>
      <Header></Header>      
      <Page title="Service Areas" subtitle="Manage the service areas for your providers">                

        {/* Session 30 — collapsible map panel */}
        <Box sx={{ marginBottom: "16px" }}>
          <BasicButton
            type="button"
            scheme="secondaryOutline"
            onClick={toggleMap}
          >
            {showMap ? "Hide map" : "Show map"}
          </BasicButton>
          {showMap && (
            <Box
              sx={{
                marginTop: "12px",
                background: "#fff",
                border: "1px solid #EBEBEB",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              {mapLoading && (
                <Box sx={{ padding: "24px", color: "#818181" }}>Loading service area geometry…</Box>
              )}
              {mapError && (
                <Box sx={{ padding: "24px", color: "#A60000" }}>{mapError}</Box>
              )}
              {!mapLoading && !mapError && mapData && (
                <ServiceAreaMap
                  serviceAreas={mapData}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onEdit={(area) => setEditServiceArea(area)}
                  height={480}
                />
              )}
            </Box>
          )}
        </Box>

        <TableData 
          api={getServiceAreas} 
          columns={[
            // Color-dot column — non-sortable, tight. Reads the palette
            // map that was built on mount from a name-sorted fetch, so
            // dots stay consistent with map polygons and modal swatches
            // regardless of what sort the user has chosen on the table.
            createColumn(
              "color",
              "",
              (row) => row?.id,
              (info) => {
                const row = info.row.original as any;
                // Prefer the palette map (stable, name-sort). Fall back
                // to the row's own color, then to a per-row derivation
                // if the palette hasn't loaded yet.
                const color =
                  paletteById[row?.id] ||
                  row?.color ||
                  colorForIndex(info.row.index);
                // Inline styles (not `sx`) so the dot is guaranteed
                // square — theme-ui's sx pipeline was letting the box
                // stretch to fill the surrounding cell.
                return (
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      minWidth: 8,
                      minHeight: 8,
                      borderRadius: "50%",
                      background: color,
                      verticalAlign: "middle",
                    }}
                  />
                );
              },
              { header: () => null, enableSorting: false, meta: { style: { width: "14px" } } }
            ),
            createColumn(
              "name",
              "Name",
              (row) => row?.name,
              (info) => {
                const row = info.row.original;
                const isSelected = selectedId === row?.id;
                return (
                  <Box
                    sx={{
                      color: "#000",
                      fontWeight: isSelected ? 600 : 400,
                      cursor: showMap ? "pointer" : "default",
                    }}
                    onClick={() => {
                      if (showMap) setSelectedId(row?.id ?? null);
                    }}
                  >
                    {info.getValue()}
                  </Box>
                );
              }
            ),
            createColumn("updatedAt","Last Updated", (row) => row?.updatedAt, (info) => moment(info.getValue()).format('MMM D, YYYY')),
            createColumn("createdAt","Last Updated", (row) => row?.createdAt, (info) => moment(info.getValue()).format('MMM D, YYYY')),
            createColumn("lastUpdateBy","Contact Email", (row) => row?.lastUpdateBy, (info) => `${info?.getValue()?.firstName || ""} ${info?.getValue()?.lastName || ""}`),
            createColumn("actions","", () => "actions", (info) => {                            
              return (
                <Box>
                  <DropDownMenu
                    toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />}
                    items={[
                      { label: "Edit", onClick: () => setEditServiceArea(info.row.original) },
                      { label: "Deactivate", onClick: () => deleteModal(info.row.original), style: { color: "var(--theme-ui-colors-danger)" } }
                    ]}
                  />
                </Box>
              );
            }, { header: () => null, enableSorting: false, meta: { style: { width: "10px" } } }),
          ]}
          refresh={refreshData} 
          actions={[<CreateModal key="1" includeButton isPrimary={true} afterCreate={() => {
            setRefreshData(true);
            setTimeout(() => { setRefreshData(false) }, 100);
            // Re-derive the stable palette map so the new area gets a
            // color slot immediately.
            loadPalette();
            // If the map is currently visible, refetch so the new area
            // appears without a browser refresh; otherwise invalidate
            // the cache for next open.
            if (showMap) {
              fetchMapData();
            } else {
              setMapData(null);
            }
          }} />]} 
          title={"All Service Areas"}
        />
      </Page>  
      {editServiceArea && <EditModal serviceArea={editServiceArea} afterUpdate={() => {
        setEditServiceArea(null);
        setRefreshData(true);
        setTimeout(() => { setRefreshData(false) }, 100);
        // Re-derive the palette map so a saved explicit color takes
        // effect everywhere immediately.
        loadPalette();
        // If the map is currently visible, refetch so the edit shows up
        // without a browser refresh; otherwise invalidate the cache for
        // next open.
        if (showMap) {
          fetchMapData();
        } else {
          setMapData(null);
        }
      }} onToggle={(open: boolean) => !open && setEditServiceArea(null)} />}
      {confirmModal}
    </>
  );
}
